import React, { useRef, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import styled from 'styled-components';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { API_URL, ROLES } from '../../../../utils/constants';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { DateRangeFilterCell } from '~/src/components/common/grid/DateRangeFilterCell';
import { DateCell } from '~/src/components/common/grid/Cells';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import GridWrapper from '~/src/components/common/GridWrapper';
import GtinComponent from '~/src/components/common/GtinComponent';
import RolesDependent from '~/src/components/common/RolesDependent';
import ProductInStockModal from '../components/ProductInStockModal';
import TableButton from '~/src/components/common/forms/Button/TableButton';

export default function Inventory(): JSX.Element {
  const stateRef = useRef(null);
  const history = useHistory();
  const url = `${API_URL}/inventory/get`;
  const StatefullGrid = useCallback(withState(Grid, url), [url]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const barcodeCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/inventory/list-for-generic/${dataItem.product.genericProductId}`}>
          <GtinComponent text="CU" code={dataItem?.cuGtin} />
          <GtinComponent text="DU" code={dataItem?.duGtin} />
        </Link>
      </td>
    );
  }, []);

  const productNameCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/inventory/list-for-generic/${dataItem.product.genericProductId}`}>
          {dataItem.product.genericProduct.name}
        </Link>
      </td>
    );
  }, []);

  const unitsCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <Link to={`/inventory/list-for-generic/${dataItem.product.genericProductId}`}>
          {`${dataItem.fullDuUnits}/${dataItem.singleDpUnits}/${dataItem.singleCuUnits}`}
        </Link>
      </td>
    );
  }, []);

  const handleOpenModal = useCallback((productId: string) => {
    setSelectedProductId(productId);
    setIsModalOpen(true);
  }, []);

  const periodInStockCell = useCallback((row: any): JSX.Element => {
    const { dataItem } = row;

    return (
      <td>
        <TableButton
          title="View Period In Stock Info"
          onClick={() => handleOpenModal(dataItem.product.genericProductId)}
        />
      </td>
    );
  }, [handleOpenModal]);

  return (
    <WhitePad isFluid>
      <TitleComponent title="Sevlievo New" />
      <RolesDependent
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
        ]}
      >
        <LinkWrapper>
          <SecondaryButton
            isBig
            onClick={(): void => history.push('/inventory/select-product')}
            title="New Incoming Products"
          />
          <SecondaryButton isBig onClick={(): void => history.push('/inventory/missing')} title="Missing" />
          <SecondaryButton isBig onClick={(): void => history.push('/inventory/move')} title="Locations" />
          <SecondaryButton
            isBig
            onClick={(): void => history.push('/inventory/purchase-order/none')}
            title="Purchase Order"
          />
          <SecondaryButton isBig onClick={(): void => history.push('/inventory/reports')} title="Reports" />
        </LinkWrapper>
      </RolesDependent>
      <SubTitle>Current Stock</SubTitle>
      <GridWrapper>
        <StatefullGrid ref={stateRef}>
          <GridColumn
            field="name"
            title="Product Name"
            cell={productNameCell} />
          <GridColumn
            field="gtin"
            title="Barcode"
            cell={barcodeCell} />
          <GridColumn
            field="totalCuUnits"
            title="Total Units" />
          <GridColumn
            field="fullDuUnits"
            title="Full Boxes"
            cell={unitsCell} />
          <GridColumn
            field="expirationDate"
            title="Expiration Date"
            filterCell={DateRangeFilterCell}
            cell={DateCell}
          />
          <GridColumn
            field="periodInStock"
            title="Period In Stock"
            sortable={false}
            filterable={false}
            cell={periodInStockCell}
          />
        </StatefullGrid>
      </GridWrapper>
      {selectedProductId && (
        <ProductInStockModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          productId={selectedProductId}
        />
      )}
    </WhitePad>
  );
}

const SubTitle = styled.h3`
  color: #a7a7a7;
  font-size: 25px;
  font-weight: 700;
  margin: 0;
  line-height: 29px;
`;

const LinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
